import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { birdToast } from '../../shared/components/core-ui/toast';
import { isErrorInfo } from '../../shared/interfaces/error-info.interface';
import { createHrWfhRequest } from '../../shared/services/data/hr.data';
import { HrDialog } from './hr-dialog.component';
import {
  BTN_PRIMARY,
  BTN_SECONDARY,
  FIELD_ERROR,
  INPUT,
  LABEL,
  TEXTAREA,
  todayIso,
  validateWfh,
  WfhFormValues
} from './hr.util';

export const WfhRequestModal: React.FC<{ onClose: () => void; onCreated: () => void }> = ({
  onClose,
  onCreated
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<WfhFormValues>(() => ({
    startDate: todayIso(),
    endDate: todayIso(),
    description: ''
  }));
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const errors = useMemo(() => validateWfh(values), [values]);
  const set = (patch: Partial<WfhFormValues>) => setValues((prev) => ({ ...prev, ...patch }));
  const errorFor = (field: keyof WfhFormValues) =>
    touched && errors[field] ? (
      <span className={FIELD_ERROR}>{t(`HR_MODULE.FORM.ERRORS.${errors[field]}`)}</span>
    ) : null;

  const submit = () => {
    setTouched(true);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    createHrWfhRequest({
      startDate: values.startDate,
      endDate: values.endDate,
      description: values.description.trim()
    }).then((result) => {
      setSaving(false);
      if (isErrorInfo(result)) {
        birdToast.error(
          result.message || t('HR_MODULE.TOASTS.FAILED'),
          t('HR_MODULE.TOASTS.TITLE')
        );
        return;
      }
      birdToast.success(t('HR_MODULE.TOASTS.WFH_CREATED'), t('HR_MODULE.TOASTS.TITLE'));
      onCreated();
    });
  };

  return (
    <HrDialog
      title={t('HR_MODULE.FORM.WFH_TITLE')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={BTN_SECONDARY} onClick={onClose}>
            {t('HR_MODULE.ACTIONS.CLOSE')}
          </button>
          <button type="button" className={BTN_PRIMARY} disabled={saving} onClick={submit}>
            {t('HR_MODULE.ACTIONS.SUBMIT')}
          </button>
        </>
      }
    >
      <div className="grid gap-[16px] sm:grid-cols-2">
        <label className={LABEL}>
          {t('HR_MODULE.FORM.START_DATE')}
          <input
            type="date"
            className={INPUT}
            value={values.startDate}
            onChange={(e) => {
              const startDate = e.target.value;
              set(
                values.endDate && startDate > values.endDate
                  ? { startDate, endDate: startDate }
                  : { startDate }
              );
            }}
          />
          {errorFor('startDate')}
        </label>
        <label className={LABEL}>
          {t('HR_MODULE.FORM.END_DATE')}
          <input
            type="date"
            className={INPUT}
            min={values.startDate || undefined}
            value={values.endDate}
            onChange={(e) => set({ endDate: e.target.value })}
          />
          {errorFor('endDate')}
        </label>
      </div>
      <label className={LABEL}>
        {t('HR_MODULE.FORM.DESCRIPTION')}
        <textarea
          className={TEXTAREA}
          placeholder={t('HR_MODULE.FORM.DESCRIPTION_PLACEHOLDER')}
          value={values.description}
          onChange={(e) => set({ description: e.target.value })}
        />
      </label>
    </HrDialog>
  );
};
